import { ApplicationConfig, importProvidersFrom, InjectionToken, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { APP_INITIALIZER } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './service/interceptor.service';
import { firstValueFrom } from 'rxjs';
import { LokalService } from './service/lokal.service';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');

export const provideTranslation = () => ({
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/i18n/', '.json');
}

/**
 * App initializer:
 * 1. Detect language from URL (/en or /vi)
 * 2. Download ZIP from Lokal API → unzip → load into TranslateService
 * 3. Apply language
 */
export function initAppFactory(translate: TranslateService, lokalService: LokalService) {
  return async () => {
    // Detect language from route
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const supportedLangs = ['en', 'vi'];
    const lang = (pathSegments.length >= 1 && supportedLangs.includes(pathSegments[0]))
      ? pathSegments[0]
      : 'en';

    // 1. First load the base translations from /i18n/<lang>.json via HttpLoader
    console.log('Detected language from route:', lang);
    await firstValueFrom(translate.use(lang)).catch((err: any) => {
      console.warn(`⚠️ Failed to set language to ${lang}`, err);
    });

    // 2. Then merge Lokal translations on top (shouldMerge=true preserves base keys)
    try {
      const translations = await lokalService.fetchTranslations();
      if (translations) {
        for (const l of Object.keys(translations)) {
          translate.setTranslation(l, translations[l], true);
        }
        console.log('✅ Lokal translations merged into TranslateService');
      }
    } catch (err: any) {
      console.error('❌ Lokal translation load failed:', err?.message || err);
    }
  };
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),
    {
      provide: APP_INITIALIZER,
      useFactory: initAppFactory,
      deps: [TranslateService, LokalService],
      multi: true,
    }
  ]
};
